import {
  AlertTriangle,
  ArrowUp,
  Bell,
  CalendarClock,
  Crown,
  Heart,
  Inbox,
  MailWarning,
  MapPin,
  Megaphone,
  Share2,
  ShieldX,
  Sparkles,
  Star,
  Store,
  UserPlus,
  UserX,
  XCircle,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Meeple } from '@/shared/components/Meeple'

/**
 * Mapa tipo → icono + colores. Cada NotificationType futura define aquí su
 * combinación visual para que el dropdown y la página /notifications los
 * pinten de forma distinta de un vistazo.
 */
export function notificationDecoration(type: string): {
  icon: ReactNode
  bgClass: string
  textClass: string
} {
  switch (type) {
    case 'RATING_AVAILABLE':
      return {
        icon: <Meeple size={18} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    case 'NEW_PARTICIPANT':
      return {
        icon: <UserPlus size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green',
        textClass: 'text-background',
      }
    case 'SESSION_REMINDER':
      return {
        icon: <CalendarClock size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-blue',
        textClass: 'text-background',
      }
    case 'SESSION_CANCELLED':
      return {
        icon: <XCircle size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-foreground',
        textClass: 'text-background',
      }
    case 'RISKY_PARTICIPANT':
      return {
        icon: <AlertTriangle size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'FAVORITE_GAME_SESSION':
      return {
        icon: <Star size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'NO_SHOW_RECORDED':
      return {
        icon: <UserX size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow-soft',
        textClass: 'text-yellow',
      }
    case 'NO_SHOW_WARNING':
      return {
        icon: <AlertTriangle size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'NO_SHOW_COOLDOWN_ACTIVATED':
      return {
        icon: <ShieldX size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    case 'RELIABILITY_DECAY':
      return {
        icon: <Sparkles size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green-soft',
        textClass: 'text-green',
      }
    case 'WAITLIST_PROMOTED':
      return {
        icon: <ArrowUp size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green',
        textClass: 'text-background',
      }
    case 'SESSION_AUTO_CANCELLED_EMPTY':
      return {
        icon: <Inbox size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow-soft',
        textClass: 'text-yellow',
      }
    case 'RELIABILITY_RESET_INACTIVITY':
      return {
        icon: <Sparkles size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green',
        textClass: 'text-background',
      }
    case 'STORE_CAMPAIGN_APPROVED':
      return {
        icon: <Megaphone size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green',
        textClass: 'text-background',
      }
    case 'STORE_CAMPAIGN_DENIED':
      return {
        icon: <Megaphone size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    case 'ADMIN_VENUE_PENDING':
    case 'ADMIN_VENUE_EDITED':
      return {
        icon: <Store size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'ADMIN_CAMPAIGN_PENDING':
      return {
        icon: <Megaphone size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'COMMUNICATION':
      return {
        icon: <Megaphone size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'ADMIN_EMAIL_QUOTA':
      return {
        icon: <MailWarning size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    case 'STORE_VENUE_APPROVED':
      return {
        icon: <Store size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green',
        textClass: 'text-background',
      }
    case 'STORE_VENUE_DENIED':
      return {
        icon: <Store size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    case 'EVENT_NEW_PARTICIPANT':
      return {
        icon: <UserPlus size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-green',
        textClass: 'text-background',
      }
    case 'EVENT_PARTICIPANT_LEFT':
      return {
        icon: <UserX size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-foreground',
        textClass: 'text-background',
      }
    case 'VENUE_SESSION_CREATED':
      return {
        icon: <Store size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-blue',
        textClass: 'text-white',
      }
    case 'VIP_GRANTED':
      return {
        icon: <Crown size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
    case 'NEW_SESSION_NEARBY':
    case 'STORE_EVENT_NEARBY':
    case 'FOLLOWED_STORE_EVENT':
      return {
        icon: <MapPin size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-blue',
        textClass: 'text-background',
      }
    case 'SESSION_EMPTY_WARNING':
      return {
        icon: <Share2 size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow-soft',
        textClass: 'text-yellow',
      }
    case 'PLAY_INTENT_GROUP':
      return {
        icon: <Heart size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    case 'PLAY_INTENT_SESSION':
      return {
        icon: <MapPin size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-red',
        textClass: 'text-white',
      }
    default:
      return {
        icon: <Bell size={16} strokeWidth={2.5} aria-hidden />,
        bgClass: 'bg-yellow',
        textClass: 'text-foreground',
      }
  }
}
